import { DeviceSyncField } from '@am3nd-org/pointcraft-types';

import { loadDeviceSessionDetailed } from 'src/utils/Services/auth-v3/session-store';
import {
  InventoryLocalDB,
  getInventorySyncMeta,
  INVENTORY_CACHE_SCHEMA_VERSION,
} from 'src/utils/Services/DB_Utils';

import { SITE_ID, SiteIdEnum } from 'src/config-global';
import { useDeviceSyncStore } from 'src/store/DeviceSync';
import { useDeviceContextStore } from 'src/store/DeviceContext';

import type { OfflineEligibility } from './types';
import { isOfflineProcessingEnabled } from './offline-payment-policy';

const REQUIRED_DEVICE_SYNC_FIELDS = [
  DeviceSyncField.BUSINESS_DATA,
  DeviceSyncField.USER_DATA,
  DeviceSyncField.TERMINALS,
  DeviceSyncField.BUSINESS_DEVICES,
];

function unavailable(
  reason: OfflineEligibility['reason'],
  businessId: string | null = null,
  inventoryCount = 0
): OfflineEligibility {
  return { eligible: false, reason, businessId, inventoryCount };
}

export async function evaluateOfflineEligibility(
  siteId: SiteIdEnum = SITE_ID
): Promise<OfflineEligibility> {
  if (siteId !== SiteIdEnum.PointOfSale) return unavailable('unsupported-app');

  const sync = useDeviceSyncStore.getState();
  const businessId = sync.businessData?.id ?? null;
  if (!businessId || sync.tenantId !== businessId) {
    return unavailable('business-context-missing', businessId);
  }
  if (!isOfflineProcessingEnabled(sync.businessData)) {
    return unavailable('business-disabled', businessId);
  }
  if (
    !REQUIRED_DEVICE_SYNC_FIELDS.every((field) => sync.availableFields.includes(field)) ||
    sync.userData.length === 0
  ) {
    return unavailable('device-sync-cache-incomplete', businessId);
  }

  try {
    const [sessionRead, inventoryMeta, inventoryCount] = await Promise.all([
      loadDeviceSessionDetailed(),
      getInventorySyncMeta(),
      InventoryLocalDB.count(),
    ]);

    const { session } = sessionRead;
    if (!session) {
      // Unreadable storage is transient; invalid data requires an authenticated
      // repair or re-pair; absent means no Auth V3 transaction identity exists.
      let reason: OfflineEligibility['reason'] = 'device-session-missing';
      if (sessionRead.status === 'unreadable') reason = 'device-session-unreadable';
      if (sessionRead.status === 'invalid') reason = 'device-session-invalid';
      return unavailable(reason, businessId, inventoryCount);
    }
    if (session.businessId !== businessId) {
      return unavailable('tenant-mismatch', businessId, inventoryCount);
    }

    const context = useDeviceContextStore.getState();
    const contextMatchesSession =
      context.businessId === businessId &&
      context.businessDeviceId === session.businessDeviceId &&
      context.isReadyForSales();
    const cachedDeviceExists = sync.businessDevices.some(
      (device) => device.id === session.businessDeviceId
    );
    const cachedTerminalMatches =
      !context.terminalId || sync.terminals.some((terminal) => terminal.id === context.terminalId);

    if (!contextMatchesSession || !cachedDeviceExists || !cachedTerminalMatches) {
      return unavailable('device-context-incomplete', businessId, inventoryCount);
    }
    if (!inventoryMeta || inventoryCount <= 0) {
      return unavailable('inventory-cache-missing', businessId, inventoryCount);
    }
    if (
      inventoryMeta.businessId !== businessId ||
      inventoryMeta.schemaVersion !== INVENTORY_CACHE_SCHEMA_VERSION ||
      inventoryMeta.recordCount !== inventoryCount
    ) {
      return unavailable('inventory-cache-stale', businessId, inventoryCount);
    }

    return { eligible: true, reason: 'eligible', businessId, inventoryCount };
  } catch {
    return unavailable('local-database-unavailable', businessId);
  }
}
